import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  socket: null,
  isConnected: false,
  roomId: null,
  currentUser: null,

  // Active users in current template
  activeUsers: [],

  // Field locks
  locks: {},

  // Cursor positions
  cursors: {},

  // Typing indicators
  typingUsers: {},

  // Presence
  lastActivity: null,
};

const rfpCollaborationSlice = createSlice({
  name: 'rfpCollaboration',
  initialState,
  reducers: {
    // Socket Connection
    setSocket: (state, action) => {
      state.socket = action.payload;
    },

    setConnected: (state, action) => {
      state.isConnected = action.payload;
    },

    setRoomId: (state, action) => {
      state.roomId = action.payload;
    },

    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },

    // Active Users
    setActiveUsers: (state, action) => {
      state.activeUsers = action.payload;
    },

    addActiveUser: (state, action) => {
      const user = action.payload;
      if (!state.activeUsers.find((u) => u.id === user.id)) {
        state.activeUsers.push(user);
      }
    },

    removeActiveUser: (state, action) => {
      const userId = action.payload;
      state.activeUsers = state.activeUsers.filter((u) => u.id !== userId);
    },

    // Locks
    setLocks: (state, action) => {
      state.locks = action.payload;
    },

    addLock: (state, action) => {
      const { field_path, user_id, user_name, locked_at } = action.payload;
      state.locks[field_path] = { user_id, user_name, locked_at };
    },

    removeLock: (state, action) => {
      const fieldPath = action.payload;
      delete state.locks[fieldPath];
    },

    clearAllLocks: (state) => {
      state.locks = {};
    },

    // Cursors
    updateCursor: (state, action) => {
      const { user_id, field_path, position } = action.payload;
      state.cursors[user_id] = { field_path, position, timestamp: Date.now() };
    },

    removeCursor: (state, action) => {
      const userId = action.payload;
      delete state.cursors[userId];
    },

    clearAllCursors: (state) => {
      state.cursors = {};
    },

    // Typing Indicators
    setTyping: (state, action) => {
      const { user_id, field_path, is_typing } = action.payload;

      if (is_typing) {
        state.typingUsers[field_path] = state.typingUsers[field_path] || [];
        if (!state.typingUsers[field_path].includes(user_id)) {
          state.typingUsers[field_path].push(user_id);
        }
      } else {
        if (state.typingUsers[field_path]) {
          state.typingUsers[field_path] = state.typingUsers[field_path].filter(
            (id) => id !== user_id
          );
          if (state.typingUsers[field_path].length === 0) {
            delete state.typingUsers[field_path];
          }
        }
      }
    },

    clearTyping: (state, action) => {
      const fieldPath = action.payload;
      delete state.typingUsers[fieldPath];
    },

    // Activity
    updateActivity: (state) => {
      state.lastActivity = Date.now();
    },

    // Disconnect
    disconnect: (state) => {
      state.isConnected = false;
      state.roomId = null;
      state.activeUsers = [];
      state.locks = {};
      state.cursors = {};
      state.typingUsers = {};
    },

    // Reset
    resetCollaboration: () => initialState,
  },
});

export const {
  setSocket,
  setConnected,
  setRoomId,
  setCurrentUser,
  setActiveUsers,
  addActiveUser,
  removeActiveUser,
  setLocks,
  addLock,
  removeLock,
  clearAllLocks,
  updateCursor,
  removeCursor,
  clearAllCursors,
  setTyping,
  clearTyping,
  updateActivity,
  disconnect,
  resetCollaboration,
} = rfpCollaborationSlice.actions;

// Selectors
export const selectIsConnected = (state) => state.rfpCollaboration.isConnected;
export const selectActiveUsers = (state) => state.rfpCollaboration.activeUsers;
export const selectLocks = (state) => state.rfpCollaboration.locks;
export const selectCursors = (state) => state.rfpCollaboration.cursors;
export const selectTypingUsers = (state) => state.rfpCollaboration.typingUsers;
export const selectRoomId = (state) => state.rfpCollaboration.roomId;

// Helper selectors
export const selectIsFieldLocked = (fieldPath) => (state) => {
  const lock = state.rfpCollaboration.locks[fieldPath];
  if (!lock) return false;

  const currentUser = state.rfpCollaboration.currentUser;
  return lock.user_id !== currentUser?.id;
};

export const selectFieldLockInfo = (fieldPath) => (state) =>
  state.rfpCollaboration.locks[fieldPath] || null;

export const selectUsersTypingAt = (fieldPath) => (state) => {
  const userIds = state.rfpCollaboration.typingUsers[fieldPath] || [];
  return state.rfpCollaboration.activeUsers.filter((u) => userIds.includes(u.id));
};

export default rfpCollaborationSlice.reducer;
